import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Eye, EyeOff, Loader2, Mail, Lock, ArrowLeft, User, CheckCircle } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { toast } from "sonner";
import { useUser } from "../context/UserContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Register() {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithToken } = useUser();
  
  // Pre-fill email if coming from login
  const [email, setEmail] = useState(location.state?.email || "");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [userType, setUserType] = useState("recruiter");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const from = location.state?.from || "/dashboard";

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error("Por favor ingresa tu email");
      return;
    }
    if (!password || password.length < 8) {
      toast.error("La contraseña debe tener al menos 8 caracteres");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, {
        email: email.toLowerCase().trim(),
        password,
        confirm_password: confirmPassword,
        user_type: userType
      });
      
      const { token, user } = response.data;
      loginWithToken(token, user);
      toast.success("¡Cuenta creada exitosamente!");
      
      // Navigate based on user type
      if (userType === "candidate") {
        navigate("/empleos", { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    } catch (error) {
      console.error("Error registering:", error);
      const detail = error.response?.data?.detail || "Error al crear la cuenta";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const passwordRequirements = [
    { met: password.length >= 8, text: "Mínimo 8 caracteres" },
    { met: password === confirmPassword && confirmPassword.length > 0, text: "Las contraseñas coinciden" }
  ];

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted p-4">
      <div className="w-full max-w-md">
        {/* Back to home */}
        <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors">
          <ArrowLeft className="h-4 w-4" />
          <span>Volver al inicio</span>
        </Link>
        
        <Card className="border-0 shadow-xl">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
              <User className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-2xl">Crear cuenta</CardTitle>
            <CardDescription>Comienza a usar TalentMatcher gratis</CardDescription>
          </CardHeader>
          
          <CardContent className="pt-4">
            <form onSubmit={handleRegister} className="space-y-4">
              {/* User Type Selection */}
              <div className="space-y-2">
                <Label>¿Qué necesitas?</Label>
                <RadioGroup value={userType} onValueChange={setUserType} className="grid grid-cols-2 gap-3">
                  <div>
                    <RadioGroupItem value="recruiter" id="recruiter" className="peer sr-only" />
                    <Label
                      htmlFor="recruiter"
                      className="flex flex-col items-center justify-center p-4 border-2 rounded-lg cursor-pointer transition-all
                        peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5
                        hover:bg-muted/50"
                    >
                      <span className="font-medium">Soy Reclutador</span>
                      <span className="text-xs text-muted-foreground mt-1">Evaluar candidatos</span>
                    </Label>
                  </div>
                  <div>
                    <RadioGroupItem value="candidate" id="candidate" className="peer sr-only" />
                    <Label
                      htmlFor="candidate"
                      className="flex flex-col items-center justify-center p-4 border-2 rounded-lg cursor-pointer transition-all
                        peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5
                        hover:bg-muted/50"
                    >
                      <span className="font-medium">Busco Empleo</span>
                      <span className="text-xs text-muted-foreground mt-1">Optimizar mi CV</span>
                    </Label>
                  </div>
                </RadioGroup>
              </div>
              
              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="tu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    required
                  />
                </div>
              </div>
              
              {/* Password */}
              <div className="space-y-2">
                <Label htmlFor="password">Contraseña</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Mínimo 8 caracteres"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10"
                    required
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              
              {/* Confirm Password */}
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirmar contraseña</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="confirm-password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Repite tu contraseña"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10"
                    required
                    minLength={8}
                  />
                </div>
              </div>
              
              {/* Password Requirements */}
              {(password.length > 0 || confirmPassword.length > 0) && (
                <div className="space-y-1">
                  {passwordRequirements.map((req, i) => (
                    <div key={i} className={`flex items-center gap-2 text-xs ${req.met ? 'text-emerald-600' : 'text-muted-foreground'}`}>
                      <CheckCircle className={`h-3 w-3 ${req.met ? 'opacity-100' : 'opacity-30'}`} />
                      <span>{req.text}</span>
                    </div>
                  ))}
                </div>
              )}
              
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creando cuenta...</>
                ) : (
                  "Crear cuenta"
                )}
              </Button>
              
              <p className="text-xs text-center text-muted-foreground">
                Al crear una cuenta, aceptas nuestros términos de servicio y política de privacidad.
              </p>
            </form>
            
            <div className="mt-6 text-center text-sm text-muted-foreground">
              ¿Ya tienes cuenta?{" "}
              <Link to="/login" className="text-primary hover:underline font-medium">
                Iniciar sesión
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
